import { useCallback, useMemo } from "react";
import {
  RESOURCE_CONNECT_EVENT,
  USER_INTERACTION_EVENT,
} from "@zapier/canvas-constants";

import { uuid } from "./utils/uuid";

declare global {
  interface Window {
    EventToolkit: {
      emit(options: Record<string, any>, event: string): any;
    };
  }
}
export function useEmitResourceConnectEvents({
  sessionId,
  canvasId,
  currentAccountId,
}: {
  sessionId: number | undefined;
  canvasId: string | undefined;
  currentAccountId: number | undefined;
}) {
  const commonProps = useMemo(
    () => ({
      customuser_id: sessionId,
      account_id: currentAccountId,
    }),
    [currentAccountId, sessionId],
  );

  const commonPropsInteractionEvent = useMemo(
    () => ({
      system_name: "canvas",
      canvas_id: canvasId,
      ...commonProps,
    }),
    [canvasId, commonProps],
  );

  const emitEventOnZapConnect = useCallback(
    (zapId?: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "select",
            interaction_goal: "Link to existing Zap",
            interaction_raw_text: null,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Zap",
            resource_id: zapId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CONNECT_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  const emitEventOnTableConnect = useCallback(
    (tableId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "select",
            interaction_goal: "Link to existing Zapier Table",
            interaction_raw_text: null,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Tables",
            resource_id: tableId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CONNECT_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  const emitEventOnInterfaceConnect = useCallback(
    (projectId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "select",
            interaction_goal: "Link to existing Zapier Interface",
            interaction_raw_text: null,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Interfaces",
            resource_id: projectId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CONNECT_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  const emitEventOnChatbotConnect = useCallback(
    (chatbotId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "select",
            interaction_goal: "Link to existing Zapier Chatbot",
            interaction_raw_text: null,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Chatbots",
            resource_id: chatbotId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CONNECT_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  /**
   * Fires observability events when an Agent is linked to the canvas.
   */
  const emitEventOnAgentConnect = useCallback(
    (agentId: string) => {
      if (window.EventToolkit?.emit) {
        const user_interaction_event_id = uuid();

        // First, emit a USER_INTERACTION_EVENT describing the action.
        window.EventToolkit.emit(
          {
            event_id: user_interaction_event_id,
            event_action: "select",
            interaction_goal: "Link to existing Zapier Agent",
            interaction_raw_text: null,
            ...commonPropsInteractionEvent,
          },
          USER_INTERACTION_EVENT,
        );

        // Then, emit the RESOURCE_CONNECT_EVENT for Functions.
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            resource_system: "Agents",
            resource_id: agentId,
            user_interaction_event_id,
            ...commonProps,
          },
          RESOURCE_CONNECT_EVENT,
        );
      }
    },
    [commonPropsInteractionEvent, commonProps],
  );

  return {
    emitEventOnZapConnect,
    emitEventOnTableConnect,
    emitEventOnInterfaceConnect,
    emitEventOnChatbotConnect,
    emitEventOnAgentConnect,
  };
}
